import React, {useState} from 'react'
import {motion} from 'framer-motion'
import {useCourses} from '../hooks/useCourses'
import {useAnalytics} from '../hooks/useAnalytics'
import SEOHead from '../components/SEOHead'
import CourseCard from '../components/ui/CourseCard'
import LoadingSpinner from '../components/ui/LoadingSpinner'
import SafeIcon from '../common/SafeIcon'
import toast from 'react-hot-toast'
import * as FiIcons from 'react-icons/fi'

const {FiSearch, FiFilter, FiMail, FiCalendar, FiArrowRight, FiBook, FiStar, FiCheck} = FiIcons

const Courses = () => {
  const {courses, loading} = useCourses()
  const {trackCTAClick, trackFormSubmission, trackSearch} = useAnalytics()
  const [searchTerm, setSearchTerm] = useState('')
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [subscribed, setSubscribed] = useState(false)

  const filteredCourses = courses.filter(course =>
    course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    course.summary?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleSearch = (value) => {
    setSearchTerm(value)
    if (value.length > 2) {
      trackSearch(value, filteredCourses.length)
    }
  }

  const handleEarlyAccessSubmit = async (e) => {
    e.preventDefault()
    if (!email) return

    setSubmitting(true)
    try {
      // Track form submission
      trackFormSubmission('waitlist_signup', {email})

      // Simulate newsletter subscription
      await new Promise(resolve => setTimeout(resolve, 1000))

      setSubscribed(true)
      toast.success('Thanks! You\'ll be the first to know when courses launch.')

      // Track successful CTA conversion
      trackCTAClick('join_waitlist', 'courses_page', 'waitlist_success')
    } catch (error) {
      toast.error('Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleBookCallClick = () => {
    trackCTAClick('book_call', 'courses_page', 'https://tidycal.com/empowernutrition/30-minute-consultation')
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title="Nutrition Courses | Expert-Led Learning | Empower Nutrition"
        description="Discover comprehensive nutrition courses designed by experts. Learn sustainable eating habits, understand macronutrients, and transform your relationship with food."
        keywords="nutrition courses, online learning, nutrition education, healthy eating courses, nutrition certification"
        url="/courses"
        image="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=1200&h=630&fit=crop"
      />

      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <motion.div
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.8}}
            className="text-center"
          >
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Nutrition Courses
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {courses.length === 0
                ? "Expert-led nutrition courses are coming soon. Get early access to our comprehensive learning platform."
                : "Discover our comprehensive collection of nutrition courses designed to help you master the science of healthy eating and wellness."
              }
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Empty State - No Courses */}
        {courses.length === 0 ? (
          <motion.div
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.8, delay: 0.2}}
            className="max-w-4xl mx-auto"
          >
            {/* Hero Section */}
            <div className="text-center mb-12">
              <div className="w-24 h-24 bg-nutrition-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <SafeIcon icon={FiBook} className="w-12 h-12 text-nutrition-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Courses are opening soon
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Want early access? Join our waitlist and be the first to know when our expert-designed nutrition courses go live.
              </p>
            </div>

            {/* Early Access Form */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 mb-8">
              <div className="max-w-md mx-auto">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                  Get Early Access
                </h3>

                {!subscribed ? (
                  <form onSubmit={handleEarlyAccessSubmit} className="space-y-4">
                    <div className="relative">
                      <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email address"
                        required
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-nutrition-600 hover:bg-nutrition-700 text-white py-3 px-4 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                    >
                      {submitting ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <>
                          <span>Join Waitlist</span>
                          <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </form>
                ) : (
                  <motion.div
                    initial={{opacity: 0, scale: 0.8}}
                    animate={{opacity: 1, scale: 1}}
                    className="text-center"
                  >
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <SafeIcon icon={FiCheck} className="w-8 h-8 text-green-600" />
                    </div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">You're on the list!</h4>
                    <p className="text-gray-600">
                      We'll notify you as soon as our courses are available.
                    </p>
                  </motion.div>
                )}

                <div className="mt-6 text-center text-sm text-gray-500">
                  No spam, ever. Unsubscribe anytime.
                </div>
              </div>
            </div>

            {/* What to Expect */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {[
                {
                  icon: FiStar,
                  title: "Expert-Led Content",
                  description: "Learn from certified nutritionists and health professionals"
                },
                {
                  icon: FiBook,
                  title: "Comprehensive Curriculum", 
                  description: "Covering everything from basics to advanced nutrition science"
                },
                {
                  icon: FiCheck,
                  title: "Practical Application",
                  description: "Real-world strategies you can implement immediately"
                }
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{opacity: 0, y: 20}}
                  animate={{opacity: 1, y: 0}}
                  transition={{duration: 0.6, delay: 0.3 + index * 0.1}}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center"
                >
                  <div className="w-12 h-12 bg-nutrition-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <SafeIcon icon={feature.icon} className="w-6 h-6 text-nutrition-600" />
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">{feature.title}</h4>
                  <p className="text-gray-600 text-sm">{feature.description}</p>
                </motion.div>
              ))}
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-nutrition-600 to-nutrition-700 rounded-2xl p-8 text-center text-white">
              <h3 className="text-2xl font-bold mb-4">
                Have Questions? Let's Talk!
              </h3>
              <p className="text-nutrition-100 mb-6 text-lg">
                Book a consultation to discuss your nutrition learning goals and get personalized course recommendations.
              </p>
              <a
                href="https://tidycal.com/empowernutrition/30-minute-consultation"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleBookCallClick}
                className="inline-flex items-center space-x-2 bg-white text-nutrition-600 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors"
              >
                <SafeIcon icon={FiCalendar} className="w-5 h-5" />
                <span>Book a Call</span>
              </a>
            </div>
          </motion.div>
        ) : (
          /* Courses Available State */
          <>
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <div className="flex-1 relative">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search courses..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
                />
              </div>
              <button className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                <SafeIcon icon={FiFilter} className="w-5 h-5" />
                <span>Filter</span>
              </button>
            </div>

            {/* Course Grid */}
            {filteredCourses.length === 0 ? (
              <div className="text-center py-12">
                <SafeIcon icon={FiSearch} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No courses found</h3>
                <p className="text-gray-500">Try adjusting your search terms</p>
              </div>
            ) : (
              <motion.div
                initial={{opacity: 0}}
                animate={{opacity: 1}}
                transition={{duration: 0.8, delay: 0.2}}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
              >
                {filteredCourses.map((course, index) => (
                  <motion.div
                    key={course.id}
                    initial={{opacity: 0, y: 50}}
                    animate={{opacity: 1, y: 0}}
                    transition={{duration: 0.8, delay: index * 0.1}}
                  >
                    <CourseCard course={course} />
                  </motion.div>
                ))}
              </motion.div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default Courses